import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CreateChargeLoanLinkageFields, ChargeWithLinkages } from "../../../api/charges";
import { selectChargeById, selectLoanReferenceIdsForCharge } from "../Logic/chargesSlice";

import ManagementModal from "../../../components/ui/modals/ManagementModal";
import LoanSelectionStep from "./LoanSelectionStep";
import ReviewChangesModal from "./ReviewChangesModal";
import { addLoanLinkages } from "../Logic/chargesThunk";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";

interface UpdateLoanModalProps {
  chargeId: string;
  onBack: () => void;
  onClose: () => void;
}

const UpdateLoanModal: React.FC<UpdateLoanModalProps> = ({
  chargeId,
  onBack,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  const { id: caseId} = useParams<{ id: string }>();

  const existingCharge = useAppSelector((state) =>
    selectChargeById(state, chargeId)
  );

  const [selectedLoans, setSelectedLoans] = useState<CreateChargeLoanLinkageFields[]>([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const excludeLoanReferenceIds = useAppSelector((state) =>
    selectLoanReferenceIdsForCharge(state, chargeId)
  );

  const updatedCharge = useMemo<ChargeWithLinkages | undefined>(() => {
    if (!existingCharge) {
      return undefined;
    }
    return {
      ...existingCharge,
      loan_linkages: [
        ...existingCharge.loan_linkages,
        ...selectedLoans.map((loan) => ({
          loan_reference_id: loan.loan_reference_id,
          charge_id: chargeId,
          linkage_id: `temp-${loan.loan_reference_id}`,
        })),
      ],
    };
  }, [existingCharge, selectedLoans,chargeId]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await dispatch(
        addLoanLinkages({
          case_id: caseId ?? "",
          charge_id: chargeId,
          request: {
              loan_linkages: selectedLoans.map((loan) => ({
                loan_reference_id: loan.loan_reference_id,
                charge_id: chargeId,
              })),
          },
        })
      ).unwrap();

      showSuccessToast("Loans added successfully.");

      onClose();
    } catch (error) {
      showErrorToast("Failed to add loans. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (showReviewModal) {
    return (
      <ReviewChangesModal
        existingCharge={existingCharge}
        updatedCharge={updatedCharge}
        onBack={() => setShowReviewModal(false)}
        onClose={onClose}
        onConfirm={handleSave}
        isLoading={isSaving}
      />
    );
  }

  return (
    <ManagementModal
      title="Add Loan"
      onClose={onClose}
      onBack={onBack}
      submitLabel="Review Changes"
      onSubmit={() => setShowReviewModal(true)}
      submitEnabled={selectedLoans.length > 0}
      isLoading={isSaving}
    >
      <div className="">
        <LoanSelectionStep
          excludeLoanReferenceIds={excludeLoanReferenceIds}
          selectedLoanIds={selectedLoans}
          onSelectionChange={setSelectedLoans}
        />
      </div>
    </ManagementModal>
  );
};

export default UpdateLoanModal;